import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { allorders, createOrder, retryPayment, verifyPayment } from "../Redux/Slice/Payment.slice";
import { motion } from "framer-motion";
import { decryptData } from "../Utils/encryption";
import { useNavigate } from "react-router-dom";
import { generateInvoice } from "../Utils/invoiceGenerator";
import { FaDownload } from "react-icons/fa";
import { IoIosCloseCircleOutline } from "react-icons/io";

const Orders = ({ viewMode }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Orders • YOYO";
    window.scrollTo(0, 0);
  }, []);

  const allOrders = useSelector((state) => state.payment.orders)

  const user = useSelector((state) => state.auth.user);
  const [showMoreFor, setShowMoreFor] = useState(null);
  // const [viewMode, setViewMode] = useState("card"); // 'card' or 'list'
  const [showAllItemsForOrder, setShowAllItemsForOrder] = useState(null); // New state to control showing all items for a specific order
  const [processingOrderId, setProcessingOrderId] = useState(null)
  const [invoiceModal, setInvoiceModal] = useState({ open: false, order: null });

  // Helper to format date
  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleViewInvoice = (order) => {
    setInvoiceModal({ open: true, order: order });
  };

  const closeInvoiceModal = () => {
    setInvoiceModal({ open: false, order: null });
  };

  const downloadFromModal = async () => {
    if (invoiceModal.order) {
      try {
        const userDetails = {
          name: user?.fullName ? decryptData(user.fullName) : "Customer",
          email: user?.email ? decryptData(user.email) : "",
        };
        await generateInvoice(invoiceModal.order, userDetails, 'save');
      } catch (error) {
        console.error("Error generating invoice for download:", error);
      }
    }
  };

  const fadeUp = {
    initial: {
      opacity: 0,
      y: 20,
    },
    animate: {
      opacity: 1,
      y: 0,
    },
  };

  // Helper functions for directional hover effect
  const getDirection = (e, element) => {
    const rect = element.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const w = rect.width;
    const h = rect.height;

    const top = y;
    const right = w - x;
    const bottom = h - y;
    const left = x;

    const min = Math.min(top, right, bottom, left);

    if (min === top) return "top";
    if (min === right) return "right";
    if (min === bottom) return "bottom";
    return "left";
  };

  const mapDirToTransform = (dir) => {
    const map = {
      top: "translate3d(0, -100%, 0)",
      right: "translate3d(100%, 0, 0)",
      bottom: "translate3d(0, 100%, 0)",
      left: "translate3d(-100%, 0, 0)",
    };
    return map[dir];
  };

  // HoverCard component
  function HoverCard({
    className = "",
    children,
    roundedClass = "rounded-xl",
  }) {
    const cardRef = useRef(null);
    const isLeavingRef = useRef(false);
    const enterRafRef = useRef(0);
    const [overlayTransform, setOverlayTransform] = useState(
      "translate3d(0, 0, 0)"
    );
    const [overlayOpacity, setOverlayOpacity] = useState(0);

    const handleEnter = (e) => {
      const dir = getDirection(e, cardRef.current);
      isLeavingRef.current = false;
      setOverlayOpacity(1);
      setOverlayTransform(mapDirToTransform(dir));
      if (enterRafRef.current) cancelAnimationFrame(enterRafRef.current);
      enterRafRef.current = requestAnimationFrame(() => {
        setOverlayTransform("translate3d(0, 0, 0)");
        enterRafRef.current = 0;
      });
    };

    const handleLeave = (e) => {
      const dir = getDirection(e, cardRef.current);
      isLeavingRef.current = true;
      if (enterRafRef.current) {
        cancelAnimationFrame(enterRafRef.current);
        enterRafRef.current = 0;
      }
      setOverlayOpacity(0);
      setOverlayTransform(mapDirToTransform(dir));
    };

    const handleTransitionEnd = () => {
      if (isLeavingRef.current) {
        setOverlayOpacity(0);
        isLeavingRef.current = false;
      }
    };

    useEffect(() => {
      return () => {
        if (enterRafRef.current) cancelAnimationFrame(enterRafRef.current);
      };
    }, []);

    return (
      <div
        ref={cardRef}
        onPointerEnter={handleEnter}
        onPointerLeave={handleLeave}
        className={`relative overflow-hidden ${roundedClass} ${className}`}
      >
        <div
          className={`pointer-events-none absolute inset-0 ${roundedClass} bg-white/10 border border-transparent transition-all duration-300 ease-out`}
          style={{
            transform: overlayTransform,
            opacity: overlayOpacity,
            willChange: "transform, opacity",
            zIndex: 1,
          }}
          onTransitionEnd={handleTransitionEnd}
        />
        {children}
      </div>
    );
  }

  const handleRetryPayment = async (order) => {
    setProcessingOrderId(order._id)
    const resultAction = await dispatch(createOrder({ amount: order?.amount }));
    if (createOrder.fulfilled.match(resultAction)) {
      const { razorpayOrder } = resultAction.payload;
      const options = {
        key: "rzp_test_hN631gyZ1XbXvp",
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: "Yoyo",
        description: "Game Purchase",
        order_id: razorpayOrder.id,
        image: "/logo556.png",
        handler: function (response) {
          console.log(response, "response");

          dispatch(verifyPayment({
            items: order.items,
            amount: order.amount,
            paymentResponse: response,
            buyNow: false,
            orderId: order._id
          })).then((res) => {
            if (verifyPayment.fulfilled.match(res)) {
              try {
                const updatedOrder = res.payload.order;
                const userDetails = {
                  name: user?.fullName ? decryptData(user.fullName) : "Customer",
                  email: user?.email ? decryptData(user.email) : "",
                };
                generateInvoice(updatedOrder, userDetails, 'open');
              } catch (e) {
                console.error("Error auto-opening invoice", e);
              }
            }
          });
        },
        prefill: {
          name: decryptData(user.fullName),
          email: decryptData(user.email),
        },
        theme: {
          color: "#7c3aed",
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
      setProcessingOrderId(null)
    }
  };

  useEffect(() => {
    dispatch(allorders());
  }, []);

  if (!allOrders || allOrders.length === 0) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center">
        <div className="text-2xl md:text-3xl font-semibold text-white mb-2">
          You have no orders yet
        </div>
        <div className="text-white/60 mb-6">
          Explore our collection and add some games to your cart.
        </div>
        <button
          onClick={() => navigate("/games")}
          className="px-6 py-3 rounded-md bg-gradient-primary transition-all"
        >
          Continue Shopping
        </button>
      </div>
    );
  }

  // console.log(allOrders,"allOrders");


  return (
    <>
      <div className="bg-[#1414145d] backdrop-blur-md border-[2px] border-white/15 text-white p-2 md:p-4 lg:p-4 rounded-lg relative ">
        {/* Conditional Rendering */}
        {viewMode === "card" ? (
          <motion.section
            variants={fadeUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="grid xl:grid-cols-3 lg:grid-cols-2 md:grid-cols-1 md600:grid-cols-2 grid-cols-1 gap-5"
          >
            {allOrders.map((order, i) => {
              return (
                <motion.div
                  key={order._id}
                  variants={fadeUp}
                  initial="initial"
                  whileInView="animate"
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                >
                  <HoverCard className="group border border-white/10 py-4 px-6 transition duration-300 ease-out hover:-translate-y-1 hover:shadow-lg backdrop-blur-sm h-64 flex flex-col justify-between">
                    {/* Order Header */}
                    <div className="flex justify-between">
                      <div className="text-2xl transition-transform duration-300 group-hover:scale-110">
                        <div
                          className={`inline-block px-3 py-1 rounded-lg text-xs font-semibold ${order.status === "paid"
                            ? "bg-emerald-300/20 text-emerald-300"
                            : "bg-yellow-300/20 text-yellow-300"
                            }`}
                        >
                          {order.status}
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1">
                        <div className="text-white font-semibold text-lg">
                          ${order.amount}
                        </div>
                        {order.status === "paid" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewInvoice(order);
                            }}
                            className="flex items-center gap-1 text-xs text-purple-300 hover:text-purple-200 transition-colors"
                            title="View Invoice"
                          >
                            <FaDownload /> Invoice
                          </button>
                        )}
                        <div className="text-gray-400 text-xs">
                          {order.currency}
                        </div>
                      </div>
                    </div>

                    {/* Order Info */}
                    <div className="mb-4">
                      <h3 className="text-white font-semibold">
                        Order #{order._id.slice(-8)}
                      </h3>
                      <p className="text-gray-400 text-sm mt-1">
                        {formatDate(order.createdAt)}
                      </p>
                    </div>

                    {/* Items Preview */}
                    <div className="space-y-3 mb-4 max-h-20 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-primary/80 scrollbar-track-transparent">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex items-center">
                          <img
                            src={item.game.cover_image?.url}
                            alt={item.game?.title}
                            className="w-10 h-10 object-cover rounded-lg border border-white/10"
                          />
                          <div className="ml-1 md600:ml-3 flex-1 min-w-0">
                            <div className="font-medium text-white text-sm truncate">
                              {item.game.title}
                            </div>
                            <div className="text-xs text-gray-400 capitalize">
                              {item.platform}
                            </div>
                          </div>
                          <div className="font-semibold text-purple-300 text-xs md600:text-sm">
                            ${item.price}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Razorpay Order ID */}
                    <div className="border-t border-white/10 pt-3">
                      {order.status === "paid" && (
                        <p className="text-gray-400 text-sm py-1">
                          <span className="font-semibold text-gray-300 tracking-wide">
                            Razorpay Order:
                          </span>{" "}
                          {order.razorpayOrderId}
                        </p>
                      )}
                    </div>
                    {order.status === "failed" && (
                      <button
                        className="px-4 text-xs  bg-gradient-primary text-white rounded"
                        onClick={() => handleRetryPayment(order)}
                        disabled={processingOrderId === order._id}
                      >
                        {processingOrderId === order._id ? "Processing.." : "Retry Payment"}
                      </button>
                    )}
                    {(order.status === "pending" || order.status === "created") && (
                      <button
                        className=" px-4 text-sm py-1 bg-gradient-primary text-white rounded"
                        onClick={() => handleRetryPayment(order)}
                        disabled={processingOrderId === order._id}
                      >
                        {processingOrderId === order._id ? "Processing.." : "Pay Now"}
                      </button>
                    )}
                  </HoverCard>
                </motion.div>
              );
            })}
          </motion.section>
        ) : (
          // List view
          <div className="overflow-x-auto rounded-lg shadow-lg backdrop-blur-md border border-white/10 h-full scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
            <table className="min-w-[900px] w-full divide-y divide-white/10">
              <thead>
                <tr className="bg-[#2A292A] sticky top-0 z-10">
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Razorpay Order ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {allOrders.map((order, idx) => (
                  <motion.tr
                    key={order._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`transition hover:bg-purple-900/20 group ${idx % 2 === 0 ? "bg-white/5" : "bg-white/0"
                      }`}
                  >
                    <td className="px-4 py-3 font-mono text-purple-300">
                      #{order._id.slice(-8)}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-emerald-300 font-semibold">
                      ${order.amount}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-lg text-xs font-bold ${order.status === "paid"
                          ? "bg-emerald-300/20 text-emerald-300"
                          : order.status === "failed"
                            ? "bg-red-400/20 text-red-400"
                            : "bg-yellow-300/20 text-yellow-300"
                          }`}
                      >
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 relative">
                      <div className="flex flex-col gap-1">
                        {(showAllItemsForOrder === order._id
                          ? order.items
                          : order.items.slice(0, 1)
                        ).map((item, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 bg-white/10 px-2 py-1 rounded text-xs text-gray-200"
                          >
                            <img
                              src={item.game.cover_image.url}
                              alt={item.game.title}
                              className="w-5 h-5 object-cover rounded"
                            />
                            {item.game.title}
                          </span>
                        ))}
                        {order.items.length > 1 &&
                          showAllItemsForOrder !== order._id && (
                            <span
                              className="text-xs text-blue-400 underline"
                              onClick={() => setShowAllItemsForOrder(order._id)}
                            >
                              +{order.items.length - 1} more
                            </span>
                          )}
                        {order.items.length > 1 &&
                          showAllItemsForOrder === order._id && (
                            <span
                              className="text-xs text-blue-400 underline"
                              onClick={() => setShowAllItemsForOrder(null)}
                            >
                              - show less
                            </span>
                          )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-400 font-mono">
                      {order.razorpayOrderId}
                    </td>
                    <td className="px-4 py-3">
                      {order.status === "failed" ? (
                        <button
                          className="px-3 py-1 bg-gradient-primary text-white rounded text-xs"
                          onClick={() => handleRetryPayment(order)}
                          disabled={processingOrderId === order._id}
                        >
                          {processingOrderId === order._id ? "Processing.." : "Retry"}
                        </button>
                      ) : '-'}
                      {(order.status === "pending" || order.status === "created") ? (
                        <button
                          className="px-3 py-1 bg-gradient-primary text-white rounded text-xs"
                          onClick={() => handleRetryPayment(order)}
                          disabled={processingOrderId === order._id}
                        >
                          {processingOrderId === order._id ? "Processing.." : "Pay Now"}
                        </button>
                      ) : null}
                      {order.status === "paid" && (
                        <button
                          className="flex items-center justify-center gap-2 px-3 py-1 bg-white/10 hover:bg-white/20 text-white rounded text-xs transition-colors"
                          onClick={() => handleViewInvoice(order)}
                          title="View Invoice"
                        >
                          <FaDownload size={10} /> Invoice
                        </button>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invoice Modal */}
      {invoiceModal.open && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 cursor-none">
          <div className="bg-[#1a1a1a] border border-white/20 rounded-2xl w-full max-w-4xl h-[90vh] flex flex-col shadow-2xl relative overflow-hidden">

            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#222]">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <span className="text-purple-500">INVOICE</span> PREVIEW
              </h3>
              <button
                onClick={closeInvoiceModal}
                className="text-white/60 hover:text-white transition-colors cursor-none"
              >
                <IoIosCloseCircleOutline size={30} />
              </button>
            </div>

            {/* Modal Body (Custom UI) */}
            <div className="flex-1 bg-[#1a1a1a] p-6 sm:p-8 overflow-y-auto">
              {invoiceModal.order && (() => {
                const order = invoiceModal.order;
                const userDetails = {
                  name: user?.fullName ? decryptData(user.fullName) : "Customer",
                  email: user?.email ? decryptData(user.email) : "",
                };

                let subtotal = 0;
                if (order.items && Array.isArray(order.items)) {
                  subtotal = order.items.reduce((sum, item) => sum + (Number(item.price) || 0), 0);
                }
                const tax = subtotal * 0.18;
                const discount = subtotal * 0.05;

                return (
                  <div className="max-w-3xl mx-auto bg-[#111] border border-white/10 text-white p-8 rounded-xl shadow-2xl min-h-[600px] flex flex-col relative overflow-hidden font-sans cursor-none">

                    {/* Watermark */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                      <h1 className="text-[12rem] font-black text-white/5 -rotate-45 select-none transform origin-center tracking-widest">
                        YOYO
                      </h1>
                    </div>

                    <div className="relative z-10 flex flex-col flex-1">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-10">
                        <div>
                          <h1 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600 mb-2 filter drop-shadow-lg">YOYO</h1>
                          <p className="text-gray-400 text-sm tracking-wider uppercase">Your Ultimate Gaming Store</p>
                        </div>
                        <div className="text-right">
                          <h2 className="text-3xl font-bold text-white mb-1 tracking-wide">INVOICE</h2>
                          <div className="h-1 w-full bg-gradient-to-r from-purple-500 to-transparent rounded-full"></div>
                        </div>
                      </div>

                      {/* Info Grid */}
                      <div className="flex justify-between mb-8 bg-white/5 p-6 rounded-lg border border-white/5 backdrop-blur-sm">
                        <div className="text-sm space-y-1">
                          <p className="text-gray-400 uppercase text-xs font-semibold tracking-wider mb-2">Invoice Details</p>
                          <p className="flex items-center gap-2"><span className="text-gray-500 w-24">Number:</span> <span className="font-mono text-white">{order._id.slice(-8).toUpperCase()}</span></p>
                          <p className="flex items-center gap-2"><span className="text-gray-500 w-24">Date:</span> <span className="text-white">{new Date(order.createdAt).toLocaleDateString()}</span></p>
                          <p className="flex items-center gap-2"><span className="text-gray-500 w-24">Status:</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${order.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'}`}>
                              {order.status}
                            </span>
                          </p>
                        </div>
                        <div className="text-right text-sm space-y-1">
                          <p className="text-gray-400 uppercase text-xs font-semibold tracking-wider mb-2">Bill To</p>
                          <h3 className="font-bold text-lg text-white">{userDetails.name}</h3>
                          <p className="text-purple-300">{userDetails.email}</p>
                        </div>
                      </div>

                      {/* Items Table */}
                      <div className="mb-8">
                        <div className="grid grid-cols-12 gap-4 bg-gradient-to-r from-purple-900/40 to-purple-900/10 p-4 font-semibold text-purple-200 rounded-t-lg border-b border-purple-500/20 text-sm uppercase tracking-wider">
                          <div className="col-span-6">Item</div>
                          <div className="col-span-3">Platform</div>
                          <div className="col-span-3 text-right">Price</div>
                        </div>
                        <div className="bg-white/5 rounded-b-lg border border-white/5">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-4 p-4 border-b border-white/5 text-sm hover:bg-white/5 transition-colors last:border-0">
                              <div className="col-span-6 font-medium text-white flex items-center gap-2">
                                {item.game?.cover_image?.url && (
                                  <img src={item.game.cover_image.url} alt="" className="w-8 h-8 rounded object-cover border border-white/10" />
                                )}
                                <span className="truncate">{item.game?.title || "Unknown Game"}</span>
                              </div>
                              <div className="col-span-3 text-gray-400 capitalize flex items-center">{item.platform}</div>
                              <div className="col-span-3 text-right font-medium text-emerald-300 flex items-center justify-end">${Number(item.price).toFixed(2)}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Totals */}
                      <div className="flex justify-end mb-12">
                        <div className="w-72 bg-[#161616] p-6 rounded-xl border border-white/10 shadow-lg">
                          <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Subtotal</span>
                              <span className="text-white">${subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Tax (18%)</span>
                              <span className="text-white">+${tax.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Discount (5%)</span>
                              <span className="text-emerald-400">-${discount.toFixed(2)}</span>
                            </div>
                            <div className="h-px bg-white/10 my-2"></div>
                            <div className="flex justify-between text-lg font-bold">
                              <span className="text-purple-400">Total</span>
                              <span className="text-white">${order.amount}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="mt-auto pt-8 border-t border-white/5 text-center">
                        <p className="text-gray-500 text-sm mb-2">Thank you for shopping with YOYO!</p>
                        <p className="text-xs text-gray-700">Generated on {new Date().toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-white/10 bg-[#222] flex justify-end gap-3">
              <button
                onClick={closeInvoiceModal}
                className="px-5 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white cursor-none transition-colors font-medium border border-white/10"
              >
                Close
              </button>
              <button
                onClick={downloadFromModal}
                className="px-6 py-2 rounded-lg bg-gradient-primary text-white font-semibold shadow-lg cursor-none hover:shadow-purple-500/20 transition-all flex items-center gap-2"
              >
                <FaDownload /> Download Invoice
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Orders;
